import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Card, Input, Logo, cn, useToast } from '@pharmaconnect/ui/web'
import { CashierLoginSchema, EmailPasswordLoginSchema } from '@pharmaconnect/utils'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import type { z } from 'zod'

import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { useTranslation } from '../i18n/I18nProvider'
import { useAuthStore } from '../stores/authStore'

type Mode = 'pharmacist' | 'cashier'

type PharmacistValues = z.infer<typeof EmailPasswordLoginSchema>
type CashierValues = z.infer<typeof CashierLoginSchema>

interface LocationState {
  from?: string
}

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const { t } = useTranslation()

  const session = useAuthStore((s) => s.session)
  const profile = useAuthStore((s) => s.profile)
  const loading = useAuthStore((s) => s.loading)
  const storeError = useAuthStore((s) => s.error)
  const login = useAuthStore((s) => s.login)
  const loginCashier = useAuthStore((s) => s.loginCashier)

  const [mode, setMode] = useState<Mode>('pharmacist')

  const pharmacistForm = useForm<PharmacistValues>({
    resolver: zodResolver(EmailPasswordLoginSchema),
    defaultValues: { email: '', password: '' },
  })

  const cashierForm = useForm<CashierValues>({
    resolver: zodResolver(CashierLoginSchema),
    defaultValues: { email: '', pin: '' },
  })

  useEffect(() => {
    if (!loading && session && profile) {
      const target = (location.state as LocationState | null)?.from ?? '/'
      navigate(target, { replace: true })
    }
  }, [loading, session, profile, location.state, navigate])

  useEffect(() => {
    if (storeError) toast.error(storeError)
  }, [storeError, toast])

  const onPharmacistSubmit = async (values: PharmacistValues) => {
    try {
      await login(values.email, values.password)
      toast.success(t('common.welcome'))
    } catch {
      // affiché via storeError
    }
  }

  const onCashierSubmit = async (values: CashierValues) => {
    try {
      await loginCashier(values.email, values.pin)
      toast.success('Bienvenue')
    } catch {
      // affiché via storeError
    }
  }

  if (!loading && session && profile) {
    return <Navigate to="/" replace />
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-pharmacist-bg px-4 py-8">
      <div className="mb-6">
        <Logo size={56} tone="dark" />
      </div>
      <Card className="w-full max-w-sm" noPadding>
        <div className="flex items-start justify-between border-b border-white/10 p-6">
          <div>
            <h1 className="text-xl font-bold text-pharmacist">{t('login.title')}</h1>
            <p className="mt-1 text-xs text-neutral-400">{t('login.subtitle')}</p>
          </div>
          <LanguageSwitcher />
        </div>

        <div
          role="tablist"
          aria-label="Mode de connexion"
          className="grid grid-cols-2 border-b border-white/10"
        >
          <TabButton active={mode === 'pharmacist'} onClick={() => setMode('pharmacist')}>
            {t('login.pharmacist_tab')}
          </TabButton>
          <TabButton active={mode === 'cashier'} onClick={() => setMode('cashier')}>
            {t('login.cashier_tab')}
          </TabButton>
        </div>

        {mode === 'pharmacist' ? (
          <form
            onSubmit={pharmacistForm.handleSubmit(onPharmacistSubmit)}
            className="flex flex-col gap-4 p-6"
            data-testid="pharmacist-form"
            noValidate
          >
            <Input
              label={t('login.email_label')}
              type="email"
              autoComplete="username"
              placeholder={t('login.email_placeholder')}
              {...pharmacistForm.register('email')}
              {...(pharmacistForm.formState.errors.email?.message
                ? { error: pharmacistForm.formState.errors.email.message }
                : {})}
            />
            <Input
              label={t('login.password_label')}
              type="password"
              autoComplete="current-password"
              placeholder={t('login.password_placeholder')}
              {...pharmacistForm.register('password')}
              {...(pharmacistForm.formState.errors.password?.message
                ? { error: pharmacistForm.formState.errors.password.message }
                : {})}
            />
            <Button type="submit" loading={pharmacistForm.formState.isSubmitting} fullWidth>
              {t('login.submit')}
            </Button>
            <Link
              to="/forgot-password"
              className="text-center text-xs text-neutral-400 hover:text-pharmacist hover:underline"
              data-testid="forgot-password-link"
            >
              {t('login.forgot_password')}
            </Link>
          </form>
        ) : (
          <form
            onSubmit={cashierForm.handleSubmit(onCashierSubmit)}
            className="flex flex-col gap-4 p-6"
            data-testid="cashier-form"
            noValidate
          >
            <Input
              label={`${t('login.cashier_tab')} — ${t('login.email_label')}`}
              type="email"
              placeholder="caissier@pharmacie.cm"
              autoComplete="email"
              spellCheck={false}
              {...cashierForm.register('email')}
              {...(cashierForm.formState.errors.email?.message
                ? { error: cashierForm.formState.errors.email.message }
                : {})}
            />
            <Input
              label="PIN"
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              pattern="[0-9]{6}"
              placeholder="••••••"
              {...cashierForm.register('pin')}
              {...(cashierForm.formState.errors.pin?.message
                ? { error: cashierForm.formState.errors.pin.message }
                : {})}
            />
            <Button
              type="submit"
              loading={cashierForm.formState.isSubmitting}
              fullWidth
              data-testid="cashier-submit"
            >
              {t('login.submit')}
            </Button>
            <p className="text-center text-xs text-neutral-500">
              {t('login.or_pin')}
            </p>
          </form>
        )}
      </Card>
    </main>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'px-4 py-3 text-sm font-medium transition',
        active
          ? 'border-b-2 border-pharmacist text-white'
          : 'text-neutral-400 hover:bg-white/[0.02] hover:text-white',
      )}
    >
      {children}
    </button>
  )
}
